import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  EditOutlined as EditOutlinedIcon,
  DocumentScanner as DocumentScannerIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridCellModes,
  GridRowModes,
  koKR,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import { v4 as uuidv4 } from 'uuid';
import { DialogTitleClose, AlertDialog } from '../dialog';
import { CustomNoRowsOverlay } from '../datagrid';
import {
  areLeadsReady,
  buildLeadJsonData,
  CLASSIFICATION_JOIN_DIVIDER,
  collectLeadKeys,
  joinClassificationParts,
} from '../GTestComparison';
import {
  CaretIntentProvider,
  markClickOffset,
  markSelectAll,
  renderComparisonTextEditCell,
} from '../GTestComparison/comparisonEditCaret';
import { gcoreServerUrl, toFilePreviewUrl } from '../../config';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as gtestComparisonActions from '../../store/gtestComparison';
import * as g04docuGCertificationActions from '../../store/g04docuGCertification';
import * as gmaterialSlotActions from '../../store/gmaterialSlot';
import { csrfFetch } from '../../store/csrf.js';
import { GlassUtils } from '../../utils';
import { processGlassLead } from './SubMatFolderClassification';
import SubMatComparisonLeadForm from './SubMatComparisonLeadForm';
import { planComparisonLeadForm, specAcceptsAnalysisFields } from './comparisonLeadPlan';

const CERT_DIVISIONS = ['RAW_MATERIAL', 'PROCESS', 'SUB_MATERIAL_PROCESS', 'SUB_MATERIAL_BUILD'];

/** 대비표는 보통 20~30행이다. 그 높이를 표 바깥에 두고, 그 안에서는 스크롤하지 않는다. */
const COMPARISON_ROW_HEIGHT = 34;
const COMPARISON_HEADER_HEIGHT = 38;
const COMPARISON_FOOTER_HEIGHT = 52;
const COMPARISON_ROW_MIN = 20;
const COMPARISON_ROW_MAX = 30;
const COMPARISON_TOOLBAR_HEIGHT = 64;

const comparisonGridHeight = (rowCount) => {
  const targetRows = Math.min(COMPARISON_ROW_MAX, Math.max(rowCount, COMPARISON_ROW_MIN));
  return COMPARISON_HEADER_HEIGHT + targetRows * COMPARISON_ROW_HEIGHT + COMPARISON_FOOTER_HEIGHT + 2;
};

/** 표지 제품명. 서버 resolveProductName과 같이 등급을 붙인다. */
const coverProductName = (base, grade) => {
  const name = base == null ? '' : String(base);
  const classification = grade == null ? '' : String(grade).trim();
  if (!classification || classification === 'no_classification' || classification.endsWith('--. 끝.')) {
    return name;
  }
  return `${name} (${classification})`;
};

/** 마스터 criterion에 lead 값 반영 (서버 applyLeadCriterionOnly와 동일 계열) */
/** 저장된 leadData의 등급 칸. 폴더 속성이 없을 때 선택값을 되돌린다. */
const partsFromSavedLead = (savedLead) => {
  const raw = savedLead?.classificationParts;
  if (Array.isArray(raw) && raw.some((part) => String(part ?? '').trim())) {
    return raw.map((part) => (part == null ? '' : String(part)));
  }
  const text = savedLead?.classification == null ? '' : String(savedLead.classification).trim();
  if (text && text !== 'no_classification') {
    return text.split(CLASSIFICATION_JOIN_DIVIDER);
  }
  return null;
};

const resolveMasterCriterion = (item, leadData) => {
  const rawCriterion = item?.criterion || '';
  if (!item || item.result === '참조값') {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  let leadField = item.leadField;
  if (!leadField || !rawCriterion) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  const m = String(leadField).trim().match(/^([a-zA-Z0-9_]+)\s*(:\s*(all|range|rangeAll)?)?/);
  if (!m) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  const key = m[1];
  const leadOption = m[3] || '';
  const leadValue = leadData?.[key] != null ? String(leadData[key]).trim() : '';
  if (!leadValue) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
  try {
    if (leadOption.startsWith('range')) {
      const num = Number(leadValue);
      if (Number.isNaN(num)) {
        return { criterion: rawCriterion, noDetailCriterion: false };
      }
      const rangeRe = /(gt|lt|gte|lte|eq)\s*:\s*(-?\d+(?:\.\d+)?)\s*(,\s*(gt|lt|gte|lte|eq)\s*:\s*(-?\d+(?:\.\d+)?))?\s*=([^;]*);?/g;
      let match;
      while ((match = rangeRe.exec(rawCriterion)) !== null) {
        const ok1 = compareLeadOper(match[1], Number(match[2]), num);
        if (!ok1) continue;
        if (match[4]) {
          if (!compareLeadOper(match[4], Number(match[5]), num)) continue;
        }
        const picked = (match[6] || '').trim();
        if (!picked) {
          return {
            criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
            noDetailCriterion: true,
          };
        }
        return { criterion: picked, noDetailCriterion: false };
      }
      return {
        criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
        noDetailCriterion: true,
      };
    }
    const escaped = leadValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const p = new RegExp(`;?\\s*${escaped}\\s*=([^;]+);?`);
    const found = rawCriterion.match(p);
    if (found) {
      return { criterion: found[1].trim(), noDetailCriterion: false };
    }
    return {
      criterion: `[평가 기준정보가 존재하지 않습니다.(${key}: ${leadValue})] ${rawCriterion}`,
      noDetailCriterion: true,
    };
  } catch (_e) {
    return { criterion: rawCriterion, noDetailCriterion: false };
  }
};

const compareLeadOper = (oper, compValue, leadValue) => {
  switch (oper) {
    case 'gte': return compValue <= leadValue;
    case 'lte': return compValue >= leadValue;
    case 'gt': return compValue < leadValue;
    case 'lt': return compValue > leadValue;
    case 'eq': return compValue === leadValue;
    default: return false;
  }
};

const isQrExtractType = (extractType) => {
  const t = String(extractType || '').toUpperCase();
  return t === 'QR' || t === 'QRCODE' || t.includes('QR');
};

/**
 * 성적서–시험성과대비표
 * 표준 선택 → lead 입력 → 분석(QR 우선, 없으면 표준 마스터 목록)
 * 저장: SubMatFolderFiles(PDF pair) + SubMatTestComparisonItems (레거시 G04docuGTestComparisons 미사용)
 */
const SubMatTestComparisonDialog = ({
  open,
  folder,
  reportFile,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const [fullScreen, setFullScreen] = useState(true);
  const [standards, setStandards] = useState([]);
  const [standard, setStandard] = useState(null);
  const [masterComparisons, setMasterComparisons] = useState([]);
  const [leadKeys, setLeadKeys] = useState([]);
  const [leadData, setLeadData] = useState({});
  const [coverItemBase, setCoverItemBase] = useState('');
  const [coverProductBase, setCoverProductBase] = useState('');
  const [itemDraft, setItemDraft] = useState('');
  const [productDraft, setProductDraft] = useState('');
  const [itemTouched, setItemTouched] = useState(false);
  const [productTouched, setProductTouched] = useState(false);
  const [coverEditOpen, setCoverEditOpen] = useState(false);
  const [coverEditItem, setCoverEditItem] = useState('');
  const [coverEditProduct, setCoverEditProduct] = useState('');
  const savedLeadPartsRef = useRef(null);
  const [classificationParts, setClassificationParts] = useState([]);
  const [gradeMount, setGradeMount] = useState(0);
  const [cert, setCert] = useState(null);
  const [certCatalog, setCertCatalog] = useState([]);
  const [pickedCertId, setPickedCertId] = useState('');
  const [materialSlots, setMaterialSlots] = useState([]);
  const [glass, setGlass] = useState(null);
  const [slotValues, setSlotValues] = useState([]);
  const [rows, setRows] = useState([]);
  const [originalRows, setOriginalRows] = useState([]);
  /** originalRows와 짝 — 작업취소 시 extractType도 같이 복원 */
  const [baselineExtractType, setBaselineExtractType] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [extractType, setExtractType] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(true);
  const [rowModesModel, setRowModesModel] = useState({});
  const [cellModesModel, setCellModesModel] = useState({});
  const [alertInfo, setAlertInfo] = useState({});
  const [specPrompt, setSpecPrompt] = useState(null);
  const apiRef = useGridApiRef();
  const caretIntentRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await csrfFetch(null, `${gcoreServerUrl}/api/gstandards`);
        const data = await response.json();
        if (!cancelled) setStandards(data?.gstandards || []);
      } catch (_e) {
        if (!cancelled) setStandards([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStandard(null);
      setMasterComparisons([]);
      setLeadKeys([]);
      setLeadData({});
      setCoverItemBase('');
      setCoverProductBase('');
      setItemDraft('');
      setProductDraft('');
      setItemTouched(false);
      setProductTouched(false);
      setCoverEditOpen(false);
      savedLeadPartsRef.current = null;
      setClassificationParts([]);
      setGradeMount(0);
      setCert(null);
      setCertCatalog([]);
      setPickedCertId('');
      setMaterialSlots([]);
      setGlass(null);
      setSlotValues([]);
      setRows([]);
      setOriginalRows([]);
      setBaselineExtractType('');
      setHasChanges(false);
      setExtractType('');
      setBusy(false);
      setLoaded(true);
      setRowModesModel({});
      setCellModesModel({});
      setAlertInfo({});
      setSpecPrompt(null);
      setFullScreen(true);
    }
  }, [open]);

  /** 말단 규격이 있으면 대비표 선행값은 그 속성으로 채운다. */
  useEffect(() => {
    if (!open || !standards.length) return;
    const spec = folder?.spec;
    if (!spec?.gstandardId) return;
    const std = standards.find((s) => s.id === spec.gstandardId) || null;
    if (std) setStandard(std);
    const specParts = Array.isArray(spec.classificationParts) ? spec.classificationParts : [];
    const fromGlass = spec.glass?.selectedGcomponentItems
      ? processGlassLead(spec.glass.selectedGcomponentItems, std?.no || '')
      : null;
    const storedThickness = spec.thickness != null ? String(spec.thickness) : '';
    setClassificationParts(specParts);
    setGradeMount((value) => value + 1);
    setGlass(spec.glass?.gglassId ? spec.glass : null);
    setSlotValues(storedThickness ? [{ code: storedThickness, name: storedThickness }] : []);
    setLeadData((ld) => ({
      ...ld,
      thickness: fromGlass?.thickness || storedThickness,
      ftThickness: fromGlass?.ftThickness || '',
      ks2004_thickness: fromGlass?.ks2004_thickness || '',
      classification: joinClassificationParts(specParts),
      classificationType0: specParts[0] || '',
      classificationParts: specParts,
    }));
  }, [open, folder?.spec, standards]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const slots = await dispatch(gmaterialSlotActions.selectAll());
        if (!cancelled) setMaterialSlots(slots || []);
      } catch (_err) {
        if (!cancelled) setMaterialSlots([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return undefined;
    const specCertId = folder?.spec?.g04docuGCertificationId;
    if (specCertId) {
      let cancelled = false;
      (async () => {
        try {
          const loaded = await g04docuGCertificationActions.selectByDirect(specCertId);
          if (!cancelled) setCert(loaded || null);
        } catch (_err) {
          if (!cancelled) setCert(null);
        }
      })();
      return () => { cancelled = true; };
    }
    let cancelled = false;
    (async () => {
      try {
        const lists = await Promise.all(CERT_DIVISIONS.map((division) => (
          g04docuGCertificationActions.selectByDivisionByQueryDirect(division)
        )));
        if (!cancelled) setCertCatalog(lists.flat().filter(Boolean));
      } catch (_err) {
        if (!cancelled) setCertCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, folder?.spec?.g04docuGCertificationId]);

  /** 저장된 대비표 행·표준·선행값 로드 (재편집) */
  useEffect(() => {
    if (!open || !folder?.id || !reportFile?.id || !standards.length) return undefined;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setLoaded(false);
      try {
        const detail = await dispatch(subMatFolderActions.selectTestComparisonDetail(
          folder.id,
          reportFile.id,
        ));
        if (cancelled) return;
        const savedLead = detail?.leadData && typeof detail.leadData === 'object'
          ? detail.leadData
          : {};
        const savedItem = typeof savedLead.coverItemName === 'string' ? savedLead.coverItemName : null;
        const savedProduct = typeof savedLead.coverProductName === 'string' ? savedLead.coverProductName : null;
        setCoverItemBase(detail?.coverItemName == null ? '' : String(detail.coverItemName));
        setCoverProductBase(detail?.coverProductBase == null ? '' : String(detail.coverProductBase));
        setItemTouched(savedItem != null);
        setItemDraft(savedItem || '');
        setProductTouched(savedProduct != null);
        setProductDraft(savedProduct || '');
        const savedRows = (detail?.g04docuGTestComparisons || []).map((row) => ({
          ...row,
          id: row.id || uuidv4(),
        }));
        const specId = folder?.spec?.gstandardId;
        if (!specId && detail?.gstandardId) {
          const std = standards.find((s) => s.id === detail.gstandardId) || null;
          if (std) setStandard(std);
        }
        const savedParts = partsFromSavedLead(savedLead);
        savedLeadPartsRef.current = savedParts;
        if (!specId) {
          const restLead = { ...savedLead };
          delete restLead.coverItemName;
          delete restLead.coverProductName;
          setLeadData(restLead);
          if (savedLead.glass?.gglassId) setGlass(savedLead.glass);
          if (savedParts) setClassificationParts(savedParts);
          if (savedLead.thickness != null && String(savedLead.thickness).trim() !== '') {
            const stored = String(savedLead.thickness);
            setSlotValues([{ code: stored, name: stored }]);
          }
          setGradeMount((value) => value + 1);
        } else {
          const spec = folder?.spec;
          const specHasGlass = Boolean(spec?.glass?.gglassId);
          const specHasParts = Array.isArray(spec?.classificationParts)
            && spec.classificationParts.some((part) => String(part ?? '').trim());
          const specHasThickness = spec?.thickness != null && String(spec.thickness).trim() !== '';
          if (!specHasGlass && savedLead.glass?.gglassId) {
            const derived = processGlassLead(
              savedLead.glass.selectedGcomponentItems,
              standards.find((item) => item.id === spec?.gstandardId)?.no || '',
            );
            setGlass(savedLead.glass);
            setLeadData((ld) => ({
              ...ld,
              thickness: specHasThickness ? ld.thickness : (derived.thickness || savedLead.thickness || ''),
              ftThickness: derived.ftThickness || '',
              ks2004_thickness: derived.ks2004_thickness || savedLead.ks2004_thickness || '',
            }));
          } else if (!specHasThickness && savedLead.thickness != null && String(savedLead.thickness).trim() !== '') {
            const stored = String(savedLead.thickness);
            setSlotValues([{ code: stored, name: stored }]);
            setLeadData((ld) => ({ ...ld, thickness: stored }));
          }
          if (!specHasParts && savedParts) {
            setClassificationParts(savedParts);
            setLeadData((ld) => ({
              ...ld,
              classification: joinClassificationParts(savedParts),
              classificationType0: savedParts[0] || '',
              classificationParts: savedParts,
            }));
            setGradeMount((value) => value + 1);
          }
        }
        if (savedRows.length) {
          setRows(savedRows);
          setOriginalRows(JSON.parse(JSON.stringify(savedRows)));
          setBaselineExtractType('SAVED');
          setHasChanges(false);
          setExtractType('SAVED');
        }
      } catch (_err) {
        // 신규 작성: 저장된 대비표 없음
      } finally {
        if (!cancelled) {
          setBusy(false);
          setLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, folder?.id, folder?.spec?.gstandardId, reportFile?.id, standards, dispatch]);

  useEffect(() => {
    if (!open || !standard?.id) {
      setMasterComparisons([]);
      setLeadKeys([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const comparisons = await gtestComparisonActions.selectAllByGstandardIdDirect(standard.id, { evaluation: true });
        if (cancelled) return;
        setMasterComparisons(comparisons || []);
        setLeadKeys(collectLeadKeys(comparisons));
      } catch (err) {
        if (!cancelled) {
          setAlertInfo({
            titleAlert: '안내',
            messageAlert: err?.serverResponse?.message || err.message || '표준 정보를 불러오지 못했습니다.',
            open: true,
          });
          setLeadKeys([]);
          setMasterComparisons([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, standard?.id]);

  const certChoices = useMemo(() => {
    if (folder?.spec?.g04docuGCertificationId || !standard?.id) return [];
    return certCatalog.filter((item) => item.gstandardId === standard.id);
  }, [folder?.spec?.g04docuGCertificationId, standard?.id, certCatalog]);

  useEffect(() => {
    if (!open || folder?.spec?.g04docuGCertificationId) return;
    const chosen = certChoices.find((item) => item.id === pickedCertId)
      || (certChoices.length === 1 ? certChoices[0] : null);
    setCert(chosen || null);
  }, [open, folder?.spec?.g04docuGCertificationId, certChoices, pickedCertId]);

  const materialSlot = useMemo(() => {
    if (!cert?.id || cert.division === 'PROCESS') return null;
    return materialSlots.find((slot) => (
      slot.certificationIds || (slot.certifications || []).map((item) => item.g04docuGCertificationId)
    ).includes(cert.id)) || null;
  }, [cert, materialSlots]);

  const gradeCount = Array.isArray(cert?.classifications) ? cert.classifications.length : 0;
  const leadPlan = useMemo(() => planComparisonLeadForm({
    leadKeys,
    division: cert?.division,
    slot: materialSlot,
    gradeCount,
  }), [leadKeys, cert?.division, materialSlot, gradeCount]);

  const hasFolderSpec = Boolean(folder?.spec?.g04docuGCertificationId);
  const glassLocked = Boolean(folder?.spec?.glass?.gglassId);
  const thicknessLocked = folder?.spec?.thickness != null && String(folder.spec.thickness).trim() !== '';
  const gradeLocked = Array.isArray(folder?.spec?.classificationParts)
    && folder.spec.classificationParts.some((part) => String(part ?? '').trim());

  useEffect(() => {
    if (!open || !cert) return;
    const needsClass = leadKeys.includes('classification');
    const needsPair = leadKeys.includes('ft_thick_class');
    if (needsClass && !needsPair && gradeCount === 0) {
      setLeadData((ld) => (ld.classification ? ld : { ...ld, classification: 'no_classification' }));
    }
  }, [open, cert, leadKeys, gradeCount]);

  const leadsReady = useMemo(
    () => areLeadsReady(leadKeys, leadData),
    [leadKeys, leadData],
  );

  const canAnalyze = Boolean(
    folder?.id && reportFile?.id && standard?.id && leadsReady && !busy,
  );

  const showBuildFromMaster = Boolean(
    standard?.id
    && masterComparisons.length
    && !isQrExtractType(extractType),
  );

  const canBuildFromMaster = showBuildFromMaster && leadsReady && !busy;

  const updateLead = (key, value) => {
    setLeadData((prev) => ({ ...prev, [key]: value }));
  };

  const applyGradeParts = (parts) => {
    const next = Array.isArray(parts) ? parts : [];
    setClassificationParts(next);
    setLeadData((ld) => ({
      ...ld,
      classification: joinClassificationParts(next),
      classificationType0: next[0] || '',
      classificationParts: next,
    }));
  };

  const applyGlass = (nextGlass) => {
    setGlass(nextGlass);
    const derived = processGlassLead(
      nextGlass?.selectedGcomponentItems,
      standard?.no || cert?.gstandardNo || '',
    );
    setLeadData((ld) => ({
      ...ld,
      thickness: derived.thickness,
      ftThickness: derived.ftThickness,
      ks2004_thickness: derived.ks2004_thickness,
    }));
  };

  const applySlotValues = (rows) => {
    setSlotValues(rows || []);
    const detail = leadPlan.thicknessDetails[0];
    const picked = (rows || []).find((row) => row?.gcomponentId === detail?.gcomponentId)
      || (rows || []).find((row) => row?.code);
    updateLead('thickness', picked?.code || '');
  };

  const specLocked = Boolean(folder?.spec?.gstandardId);

  const buildJsonData = () => buildLeadJsonData(leadKeys, leadData);

  const applyExtractRows = (list, type) => {
    const newRows = (list || []).map((row) => ({
      ...row,
      id: row.id || uuidv4(),
    }));
    const nextType = type || '';
    setRows(newRows);
    setOriginalRows(JSON.parse(JSON.stringify(newRows)));
    setBaselineExtractType(nextType);
    setHasChanges(false);
    setExtractType(nextType);
    return newRows;
  };

  const handleAnalyze = async () => {
    if (!folder?.id || !reportFile?.id) return;
    if (!standard?.id) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '먼저 표준번호를 선택하세요. 표준을 기준으로 선행 입력란과 평가 기준이 결정됩니다.',
        open: true,
      });
      return;
    }
    if (!leadsReady) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '선행값(두께·등급 등)을 모두 입력한 뒤 성적서를 분석할 수 있습니다.',
        open: true,
      });
      return;
    }
    // 분석: QR 1순위 → 실패·비QR이면 표준 마스터(GTestComparisons)+lead로 목록 구성 (서버 fillTestComparisons)
    setBusy(true);
    setLoaded(false);
    try {
      const result = await dispatch(subMatFolderActions.extractTestComparison(folder.id, {
        gstandardId: standard.id,
        reportFileId: reportFile.id,
        jsonData: buildJsonData(),
      }));
      const list = result?.g04docuGTestComparisons || [];
      applyExtractRows(list, result?.extractType);
      if (!list.length) {
        setAlertInfo({
          titleAlert: '안내',
          messageAlert: '분석 결과가 없습니다. 선행 입력값(두께·등급 등)을 확인한 뒤 다시 분석해 주세요.',
          open: true,
        });
      }
    } catch (err) {
      setRows([]);
      setOriginalRows([]);
      setBaselineExtractType('');
      setExtractType('');
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: err?.serverResponse?.message || err.message || '분석에 실패했습니다.',
        open: true,
      });
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  };

  const handleClickAdd = () => {
    const id = uuidv4();
    setRows((oldRows) => [
      ...oldRows,
      { id, item: '시험항목', unit: '단위', criterion: '기준', value: '', result: '', method: '', isNew: true },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.View, fieldToFocus: 'item' },
    }));
    setHasChanges(true);
  };

  const handleDeleteClick = (id) => () => {
    const api = apiRef.current;
    const editModel = typeof api?.getEditRowsModel === 'function' ? api.getEditRowsModel() : {};
    Object.keys(editModel || {}).forEach((rowId) => {
      Object.keys(editModel[rowId] || {}).forEach((field) => {
        try {
          api.commitCellChange({ id: rowId, field });
          api.setCellMode(rowId, field, 'view');
        } catch (_e) {
          // 편집 중이 아니면 무시
        }
      });
    });
    setRows((prev) => prev.filter((row) => row.id !== id));
    setHasChanges(true);
  };

  const handleUndo = () => {
    if (originalRows.length > 0) {
      setRows(JSON.parse(JSON.stringify(originalRows)));
      setExtractType(baselineExtractType);
      setHasChanges(false);
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '수정한 내용이 원래의 상태로 되돌려졌습니다.',
        open: true,
      });
    } else {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: '되돌릴 원본 데이터가 없습니다.',
        open: true,
      });
    }
  };

  /** 표준 마스터 항목으로 목록 구성 (QR 실패·비QR 시). lead로 기준 해석. 화면만 변경. */
  const applyMasterRows = () => {
    if (!masterComparisons.length) return;
    const json = buildJsonData();
    const newRows = masterComparisons.map((c) => {
      const resolved = resolveMasterCriterion(c, json);
      return {
        id: uuidv4(),
        item: c.item,
        unit: c.unit,
        criterion: resolved.criterion,
        value: '',
        result: c.result === '참조값' ? '참조값' : '',
        method: c.method || '',
        office: null,
        noDetailCriterion: resolved.noDetailCriterion,
      };
    });
    // originalRows·baselineExtractType은 유지 → 「작업취소」로 이전 목록 복원
    setRows(newRows);
    setHasChanges(true);
    setExtractType('MASTER');
  };

  const handleBuildFromMasterClick = () => {
    if (!canBuildFromMaster) {
      if (!leadsReady) {
        setAlertInfo({
          titleAlert: '안내',
          messageAlert: '선행값(두께·등급 등)을 모두 입력한 뒤 「표준항목으로 구성」을 사용할 수 있습니다.',
          open: true,
        });
      }
      return;
    }
    setAlertInfo({
      type: 'confirm',
      titleAlert: '표준항목으로 구성',
      messageAlert: (
        <span>
          선택한 표준의 시험항목으로 목록을 다시 구성합니다.
          <br />
          선행값(두께·등급 등)에 맞는 기준이 적용되며, 현재 목록(QR·편집 내용)은 사라집니다.
          <br />
          <br />
          <strong>지금은 화면만 바뀝니다.</strong>
          {' '}
          「저장 및 PDF 작성」을 눌러야 데이터와 PDF에 최종 반영됩니다.
          <br />
          <br />
          계속하시겠습니까?
        </span>
      ),
      open: true,
      confirmText: '구성',
      cancelText: '취소',
      handleConfirm: () => {
        applyMasterRows();
      },
    });
  };

  const coverGrade = joinClassificationParts(classificationParts);
  const itemValue = itemTouched ? itemDraft : coverItemBase;
  const productValue = productTouched ? productDraft : coverProductName(coverProductBase, coverGrade);
  const coverSummary = [itemValue, productValue].filter((text) => String(text || '').trim()).join(' · ');

  const openCoverEdit = () => {
    setCoverEditItem(itemValue);
    setCoverEditProduct(productValue);
    setCoverEditOpen(true);
  };

  const applyCoverEdit = () => {
    const nextItem = coverEditItem.trim();
    const nextProduct = coverEditProduct.trim();
    const derivedProduct = coverProductName(coverProductBase, coverGrade);
    if (!nextItem || nextItem === coverItemBase) {
      setItemTouched(false);
      setItemDraft('');
    } else {
      setItemTouched(true);
      setItemDraft(nextItem);
    }
    if (!nextProduct || nextProduct === derivedProduct) {
      setProductTouched(false);
      setProductDraft('');
    } else {
      setProductTouched(true);
      setProductDraft(nextProduct);
    }
    setCoverEditOpen(false);
  };

  const buildSavedLead = () => {
    const data = buildLeadJsonData(leadKeys, leadData);
    if (glass?.gglassId) data.glass = glass;
    if (classificationParts.some((part) => String(part ?? '').trim())) {
      data.classificationParts = classificationParts;
    }
    if (itemTouched && itemValue.trim()) data.coverItemName = itemValue.trim();
    if (productTouched && productValue.trim()) data.coverProductName = productValue.trim();
    return data;
  };

  const buildSpecPatch = () => {
    const spec = folder?.spec;
    if (!specAcceptsAnalysisFields(spec, cert?.division)) return null;
    if (cert?.division === 'PROCESS' && !(glassLocked ? spec.glass : glass)?.gglassId) return null;
    const next = { ...spec };
    const labels = [];
    if (leadPlan.showGlass && !glassLocked && glass?.gglassId) {
      const items = glass.selectedGcomponentItems || [];
      const derived = processGlassLead(items, standard?.no || cert?.gstandardNo || '');
      const [specification, , specificationByGStandard] = GlassUtils.getSpecification(items);
      next.glass = glass;
      next.thickness = derived.thickness || '';
      next.code = specification || '';
      next.codeByStandard = specificationByGStandard || '';
      labels.push('유리 구성');
    }
    if (leadPlan.showSlot && !thicknessLocked) {
      const detail = leadPlan.thicknessDetails[0];
      const picked = (slotValues || []).find((row) => row?.gcomponentId === detail?.gcomponentId)
        || (slotValues || []).find((row) => row?.code);
      if (picked?.code) {
        next.thickness = String(picked.code);
        labels.push('두께');
      }
    }
    if (leadPlan.showPlain && !thicknessLocked && String(leadData.thickness || '').trim()) {
      next.thickness = String(leadData.thickness).trim();
      labels.push('두께');
    }
    if (leadPlan.showGrade && !gradeLocked && classificationParts.some((part) => String(part ?? '').trim())) {
      next.classificationParts = classificationParts;
      labels.push('등급');
    }
    if (!labels.length) return null;
    return { spec: next, labels };
  };

  const writeComparison = async (specPatch) => {
    if (!folder?.id || !reportFile?.id || !rows.length) return;
    setBusy(true);
    setLoaded(false);
    try {
      if (specPatch) {
        await dispatch(subMatFolderActions.saveFolderSpec(folder.id, specPatch));
      }
      await dispatch(subMatFolderActions.createTestComparisonPdf(folder.id, {
        g04docuGTestComparisons: rows,
        itemName: itemValue.trim(),
        productName: productValue.trim(),
        reportFileId: reportFile.id,
        gstandardId: standard?.id || null,
        jsonData: buildSavedLead(),
      }));
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: (
          <div>
            <span style={{ color: '#1976d2' }}>시험성과대비표</span>
            <span> 데이터와 PDF가 저장되었습니다.</span>
          </div>
        ),
        open: true,
      });
      setHasChanges(false);
      setOriginalRows(JSON.parse(JSON.stringify(rows)));
      setBaselineExtractType(extractType || 'SAVED');
      onSaved?.(folder.id);
    } catch (err) {
      setAlertInfo({
        titleAlert: '안내',
        messageAlert: err?.serverResponse?.message || err.message || '저장 및 PDF 작성에 실패했습니다.',
        open: true,
      });
    } finally {
      setBusy(false);
      setLoaded(true);
    }
  };

  const handleSavePdf = () => {
    if (!folder?.id || !reportFile?.id || !rows.length || busy) return;
    const pending = buildSpecPatch();
    if (pending) {
      setSpecPrompt(pending);
      return;
    }
    writeComparison(null);
  };

  const processRowUpdate = useCallback((newRow) => {
    setRows((prev) => prev.map((row) => (row.id === newRow.id ? newRow : row)));
    setHasChanges(true);
    return newRow;
  }, []);

  /** 레거시와 동일: 셀 커밋 시 rows에 바로 반영 (다른 행 삭제 시 수정값 유지) */
  const handleCellEditCommit = useCallback((params) => {
    const { id, field, value } = params;
    setRows((prev) => prev.map((row) => (
      row.id === id ? { ...row, [field]: value } : row
    )));
    setHasChanges(true);
  }, []);

  const handleCellKeyDown = (params, event) => {
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && event.key === ' '
      && !event.shiftKey
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
    ) {
      event.defaultMuiPrevented = true;
      event.preventDefault();
      markSelectAll(caretIntentRef, params.id, params.field);
      apiRef.current.setCellMode(params.id, params.field, 'edit');
      return;
    }
    if (params.cellMode !== 'edit' && params.isEditable && event.key === 'Enter') {
      markSelectAll(caretIntentRef, params.id, params.field);
    }
    // 보기 상태의 글자 키는 편집을 열지 않는다. 입력칸은 엔터나 스페이스로 만든 뒤에만 친다.
    if (
      params.cellMode !== 'edit'
      && params.isEditable
      && !event.ctrlKey
      && !event.metaKey
      && !event.altKey
      && event.key.length === 1
    ) {
      event.defaultMuiPrevented = true;
      return;
    }
    if (event.key !== 'Enter' || params.cellMode !== 'edit') return;
    const updatedRow = { ...params.row, [params.field]: event.target.value };
    processRowUpdate(updatedRow);
    setCellModesModel((prevModel) => ({
      ...prevModel,
      [params.id]: {
        ...prevModel[params.id],
        [params.field]: { mode: GridCellModes.View },
      },
    }));
  };

  const handleCellClick = useCallback((params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) return;
    const focusedCell = apiRef.current.state?.focus?.cell;
    if (!focusedCell || focusedCell.id !== params.id || focusedCell.field !== params.field) return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
    apiRef.current.setCellMode(params.id, params.field, 'edit');
  }, [apiRef]);

  const handleCellDoubleClick = useCallback((params, event) => {
    if (!params.isEditable || params.cellMode === 'edit') return;
    markClickOffset(caretIntentRef, params.id, params.field, event.clientX, event.clientY);
  }, []);

  const handleRowOrderChange = (params) => {
    const { oldIndex, targetIndex } = params;
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setHasChanges(true);
  };

  const columns = useMemo(() => [
    {
      field: 'item',
      headerName: '구분',
      editable: true,
      width: 160,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'unit',
      headerName: '단위',
      editable: true,
      width: 100,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'criterion',
      headerName: '기준',
      editable: true,
      width: 300,
      renderEditCell: renderComparisonTextEditCell,
      renderCell: (params) => {
        if (params.row.noDetailCriterion) {
          return (
            <Tooltip title="선행값에 맞는 평가 기준정보가 없습니다.">
              <span style={{ color: '#DB4E2E', fontWeight: 'bold' }}>{params.value}</span>
            </Tooltip>
          );
        }
        return params.value;
      },
    },
    {
      field: 'value',
      headerName: '시험결과',
      editable: true,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'result',
      headerName: '판정',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: ['합격', '불합격', '참조값'],
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.value === '합격') {
          return <span style={{ color: '#1976d2', fontWeight: 'bold' }}>합격</span>;
        }
        if (params.value === '불합격') {
          return <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>불합격</span>;
        }
        return params.value;
      },
    },
    {
      field: 'method',
      headerName: '시험방법',
      editable: true,
      width: 120,
      renderEditCell: renderComparisonTextEditCell,
    },
    {
      field: 'office',
      headerName: '시험기관',
      hide: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '기능',
      width: 87,
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        if (row.office) return [];
        return [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ], []);

  // documentPath 고정 시 URL 고정 — lead 입력 리렌더마다 난수 query로 iframe PDF가 재로딩되지 않게
  const reportPreviewUrl = useMemo(
    () => (reportFile?.documentPath ? toFilePreviewUrl(reportFile.documentPath) : ''),
    [reportFile?.documentPath],
  );

  const reportLabel = reportFile?.fileName || reportFile?.id || '';
  const gridHeight = comparisonGridHeight(rows.length);
  const splitHeight = COMPARISON_TOOLBAR_HEIGHT + gridHeight;

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open={open}
        disableEscapeKeyDown
        onClose={busy ? undefined : onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: fullScreen ? '100vh' : '90vh',
            maxHeight: fullScreen ? '100vh' : '90vh',
          },
        }}
      >
        <DialogTitleClose
          onClose={busy ? undefined : onClose}
          onMinMax={() => setFullScreen((v) => !v)}
          fullScreen={fullScreen}
          sx={{
            backgroundColor: fullScreen ? '#1976d2' : 'transparent',
            color: fullScreen ? 'white' : 'inherit',
            m: 0,
            py: 0.75,
            pl: 2,
            pr: 10,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flexShrink: 0, lineHeight: 1.4 }}>
              {reportFile?.comparisonFile || extractType === 'SAVED'
                ? '시험성과대비표 수정'
                : '시험성과대비표 만들기'}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{ minWidth: 0, opacity: 0.9, color: fullScreen ? 'inherit' : 'text.secondary' }}
            >
              {[folder?.name, reportLabel].filter(Boolean).join(' — ')}
            </Typography>
          </Box>
        </DialogTitleClose>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                sx={{
                  width: 280,
                  maxWidth: '100%',
                  flex: '0 0 auto',
                  '& .MuiInputLabel-root': { fontSize: '0.9rem' },
                  '& .MuiOutlinedInput-root': { height: 40 },
                  '& .MuiInputBase-input': { fontSize: '0.9rem', py: 0 },
                }}
                options={standards}
                value={standard}
                onChange={(_e, v) => {
                  setStandard(v);
                  setPickedCertId('');
                  setGlass(null);
                  setSlotValues([]);
                  setLeadData({});
                  savedLeadPartsRef.current = null;
                  setClassificationParts([]);
                  setRows([]);
                  setOriginalRows([]);
                  setBaselineExtractType('');
                  setHasChanges(false);
                  setExtractType('');
                }}
                getOptionLabel={(o) => {
                  if (!o) return '';
                  const no = o.no || o.code || '';
                  return no ? `${no} — ${o.name || ''}` : (o.name || o.id || '');
                }}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                disabled={specLocked}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="표준번호"
                    size="small"
                    required
                  />
                )}
              />
              {certChoices.length > 1 && (
                <FormControl
                  size="small"
                  sx={{
                    width: 240,
                    maxWidth: '100%',
                    flex: '0 0 auto',
                    '& .MuiInputLabel-root': { fontSize: '0.9rem' },
                    '& .MuiOutlinedInput-root': { height: 40 },
                    '& .MuiSelect-select': { fontSize: '0.9rem', py: 0, display: 'flex', alignItems: 'center' },
                  }}
                >
                  <InputLabel id="comparison-cert-label">인증규격</InputLabel>
                  <Select
                    labelId="comparison-cert-label"
                    label="인증규격"
                    value={cert?.id || ''}
                    onChange={(e) => {
                      setPickedCertId(e.target.value);
                      setGlass(null);
                      setSlotValues([]);
                    }}
                  >
                    {certChoices.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.gstandardNo ? `${item.gstandardNo} — ` : ''}{item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <SubMatComparisonLeadForm
                hasStandard={Boolean(standard?.id)}
                cert={cert}
                certMessage={
                  hasFolderSpec && !cert
                    ? '인증규격을 확인하는 중입니다.'
                    : certChoices.length > 1 && !cert
                      ? '인증규격을 선택하면 입력란이 표시됩니다.'
                      : !hasFolderSpec && standard?.id && certCatalog.length === 0
                        ? '인증규격을 확인하는 중입니다.'
                        : !hasFolderSpec && standard?.id && certChoices.length === 0
                          ? '이 표준에 연결된 인증규격이 없습니다.'
                          : ''
                }
                plan={leadPlan}
                busy={busy}
                glass={glass}
                onGlass={applyGlass}
                glassLocked={glassLocked}
                slot={materialSlot}
                slotValues={slotValues}
                onSlot={applySlotValues}
                slotLocked={thicknessLocked}
                plainThickness={leadData.thickness == null ? '' : String(leadData.thickness)}
                onPlainThickness={(value) => updateLead('thickness', value)}
                plainLocked={thicknessLocked}
                gradeLocked={gradeLocked}
                gradeText={joinClassificationParts(folder?.spec?.classificationParts)}
                gradeSeed={classificationParts}
                gradeKey={`${cert?.id || 'none'}:${gradeMount}`}
                onGradeParts={applyGradeParts}
              />
              <Tooltip
                title={
                  !standard?.id
                    ? '먼저 표준번호를 선택하세요.'
                    : !leadsReady
                      ? '선행값(두께·등급 등)을 모두 입력해야 분석할 수 있습니다.'
                      : '성적서를 분석하여 시험성과대비표 목록을 구성합니다. (QR 우선)'
                }
              >
                <span style={{ marginLeft: 16 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DocumentScannerIcon />}
                    onClick={handleAnalyze}
                    disabled={!canAnalyze}
                    sx={{
                      height: 40,
                      py: 0,
                      boxSizing: 'border-box',
                      '& .MuiButton-startIcon': { marginRight: 0.75 },
                      '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 18 },
                    }}
                  >
                    성적서 분석
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
          {leadPlan?.notices?.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
              {leadPlan.notices.join(' ')}
            </Typography>
          )}

          {(busy || !loaded) && <LinearProgress />}

          <Card sx={{ mx: 1.5, my: 1, p: 1.5, flex: '0 0 auto', display: 'flex', overflow: 'hidden' }}>
            <Box sx={{ width: '100%', height: splitHeight }}>
            <PanelGroup direction="horizontal" style={{ height: '100%' }}>
              <Panel defaultSize={40} minSize={20} maxSize={80}>
                <Box sx={{ pr: 2, height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold', mb: 1, flexShrink: 0 }}>
                    ∙ 성적서
                  </Typography>
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    {reportPreviewUrl ? (
                      <iframe
                        title="성적서 미리보기"
                        src={reportPreviewUrl}
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                          border: '1px solid #ddd',
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          border: '1px solid #ddd',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          성적서가 없습니다.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Panel>

              <PanelResizeHandle
                style={{
                  width: 8,
                  backgroundColor: '#e0e0e0',
                  cursor: 'col-resize',
                }}
              />

              <Panel defaultSize={60} minSize={20} maxSize={80}>
                <Box sx={{ pl: 1.5, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: '1 1 240px' }}>
                      <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold', flexShrink: 0 }}>
                        ∙ 시험성과대비표
                      </Typography>
                      <Tooltip title={coverSummary || 'PDF 표지'}>
                        <Box
                          component="span"
                          sx={{
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                          }}
                        >
                          {coverSummary}
                        </Box>
                      </Tooltip>
                      <Tooltip title="PDF 표지의 항목·제품명을 고칩니다.">
                        <IconButton size="small" onClick={openCoverEdit} aria-label="PDF 표지 편집" sx={{ flexShrink: 0 }}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        flex: '0 1 auto',
                      }}
                    >
                      <Tooltip title="목록의 마지막에 시험항목 한 행을 추가합니다.">
                        <Button color="primary" startIcon={<AddIcon />} onClick={handleClickAdd} size="small">
                          추가
                        </Button>
                      </Tooltip>
                      <Tooltip title="시험성과대비표 행 데이터와 PDF를 함께 저장합니다.">
                        <Button
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={handleSavePdf}
                          size="small"
                          disabled={busy || rows.length === 0 || !reportFile?.id}
                        >
                          저장 및 PDF 작성
                        </Button>
                      </Tooltip>
                      <Tooltip title="수정하신 내용을 취소하고 원래의 상태로 되돌립니다.">
                        <Button
                          color="primary"
                          startIcon={<UndoIcon />}
                          onClick={handleUndo}
                          disabled={!hasChanges}
                          size="small"
                        >
                          작업취소
                        </Button>
                      </Tooltip>
                      {showBuildFromMaster && (
                        <Tooltip
                          title={
                            leadsReady
                              ? '표준 시험항목으로 목록을 구성합니다. 선행값에 맞는 기준이 적용됩니다.'
                              : '선행값(두께·등급 등)을 모두 입력해야 사용할 수 있습니다.'
                          }
                        >
                          <span>
                            <Button
                              color="primary"
                              startIcon={<RefreshIcon />}
                              onClick={handleBuildFromMasterClick}
                              size="small"
                              disabled={!canBuildFromMaster}
                            >
                              표준항목으로 구성
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ height: gridHeight, flexShrink: 0, width: '100%' }}>
                    <CaretIntentProvider value={caretIntentRef}>
                    <DataGridPro
                      localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                      getCellClassName={(params) => {
                        if (params.row.office && !['criterion', 'result', 'method'].includes(params.field)) {
                          return 'disabled-row';
                        }
                        return '';
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.85em',
                        height: '100%',
                        ...(rows.length <= COMPARISON_ROW_MAX ? {
                          '& .MuiDataGrid-virtualScroller': { overflowY: 'hidden' },
                        } : {}),
                        '& .disabled-row': {
                          '&:hover': { backgroundColor: '#eeeeee' },
                          opacity: 0.85,
                          cursor: 'not-allowed',
                        },
                      }}
                      slots={{
                        noRowsOverlay: CustomNoRowsOverlay,
                        loadingOverlay: LinearProgress,
                      }}
                      headerHeight={COMPARISON_HEADER_HEIGHT}
                      rowHeight={COMPARISON_ROW_HEIGHT}
                      rows={rows}
                      columns={columns}
                      loading={!loaded}
                      disableColumnMenu
                      rowReordering
                      editMode="cell"
                      isCellEditable={(params) => {
                        if (params.field === 'criterion' || params.field === 'result' || params.field === 'method') {
                          return true;
                        }
                        return !params.row.office;
                      }}
                      apiRef={apiRef}
                      onCellModesModelChange={setCellModesModel}
                      onCellClick={handleCellClick}
                      onCellDoubleClick={handleCellDoubleClick}
                      onCellEditCommit={handleCellEditCommit}
                      onCellKeyDown={handleCellKeyDown}
                      processRowUpdate={processRowUpdate}
                      rowModesModel={rowModesModel}
                      onRowModesModelChange={setRowModesModel}
                      cellModesModel={cellModesModel}
                      onRowOrderChange={handleRowOrderChange}
                    />
                    </CaretIntentProvider>
                  </Box>
                </Box>
              </Panel>
            </PanelGroup>
            </Box>
          </Card>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={busy}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={coverEditOpen} onClose={() => setCoverEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>PDF 표지</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            시험성과대비표 PDF 표지에 들어갑니다. 비우면 계산된 기본값으로 돌아갑니다.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              size="small"
              label="항목"
              value={coverEditItem}
              onChange={(e) => setCoverEditItem(e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="제품명"
              value={coverEditProduct}
              onChange={(e) => setCoverEditProduct(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCoverEditOpen(false)}>취소</Button>
          <Button variant="contained" onClick={applyCoverEdit}>확인</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        alertInfo={alertInfo}
        setAlertInfo={setAlertInfo}
      />
      <Dialog open={Boolean(specPrompt)} onClose={busy ? undefined : () => setSpecPrompt(null)}>
        <DialogTitle>폴더 속성</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`이번에 입력한 ${specPrompt?.labels?.join('·') || '값'}을 폴더 속성에도 저장하면 다음부터 그 칸이 고정됩니다. 프로젝트에서 성적서를 찾을 때도 이 값을 씁니다.`}
            <br />
            이번 대비표에만 두면 폴더 속성은 바뀌지 않고, 이 대비표를 다시 열면 입력한 내용이 보입니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const spec = specPrompt?.spec;
              setSpecPrompt(null);
              writeComparison(spec);
            }}
            disabled={busy}
          >
            폴더 속성에 반영
          </Button>
          <Button
            onClick={() => {
              setSpecPrompt(null);
              writeComparison(null);
            }}
            disabled={busy}
          >
            이번 대비표만
          </Button>
          <Button onClick={() => setSpecPrompt(null)} disabled={busy} color="inherit">
            취소
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubMatTestComparisonDialog;
